// File: typescript/apps/front-end/src/main/react/distinction/DistinctionBadge.tsx
import React from "react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import type { Localizer } from "@/main/react/Types";
import { getDistinctionCopy, VOICE_OF_MONTH_HOOK, VOICE_OF_MONTH_ICON } from "@/main/react/distinction/distinctionCopy";

/**
 * The byline distinction: ONE small icon beside an author's name, with its name and meaning in a
 * tooltip (distinctions design §5, work order D4/A6/A8, slice `2a`).
 *
 * <p>Both inputs arrive already resolved by the server (design §6.1): `featuredDistinction` is the
 * member's pick or the rarest they hold (null when they chose "None" or hold nothing), and
 * `voiceOfMonthGroupName` is set only when the author is the current Voice of the month in the post's one
 * readable group. A6: Voice of the month takes the slot when both are present. Absent means render
 * nothing, and an unknown key (a newer server) also renders nothing rather than a blank icon.
 *
 * <p>An icon, never a text chip, so it stays distinct from `GroupLeadershipBadge` and
 * `GroupMemberFlairChip` (A31 of the 2026-09-10 order), which are pills. It is a real `<button>` so the
 * tooltip is reachable by keyboard and, by tap, on a phone where hover does not exist; its accessible
 * name carries the name AND the meaning, so a screen reader does not depend on the tooltip at all.
 * `data-distinction` is the stable hook (`VOICE_OF_MONTH` for the monthly pick).
 */
export function DistinctionBadge({
    localizer,
    featuredDistinction,
    voiceOfMonthGroupName,
    playwrightId = "author-distinction",
    className,
}: {
    localizer: Localizer;
    featuredDistinction?: string | null;
    voiceOfMonthGroupName?: string | null;
    playwrightId?: string;
    className?: string;
}) {
    const [open, setOpen] = React.useState(false);
    const groupName = voiceOfMonthGroupName?.trim();
    const copy = groupName ? null : getDistinctionCopy(featuredDistinction);

    if (!groupName && !copy) {
        return null;
    }

    const name = groupName
        ? localizer("distinctions.voiceOfMonth.name", groupName)
        : localizer(copy!.nameKey);
    const meaning = groupName
        ? localizer("distinctions.voiceOfMonth.meaning")
        : localizer(copy!.meaningKey);
    const Icon = groupName ? VOICE_OF_MONTH_ICON : copy!.icon;
    const hook = groupName ? VOICE_OF_MONTH_HOOK : String(featuredDistinction);

    return (
        <Tooltip open={open} onOpenChange={setOpen}>
            <TooltipTrigger asChild>
                <button
                    type="button"
                    aria-label={`${name}. ${meaning}`}
                    data-distinction={hook}
                    data-playwright-id={playwrightId}
                    // A tap toggles: a touch pointer never hovers, so without this the meaning would be
                    // unreachable on the phone, which is where most replies are read.
                    onClick={() => setOpen((current) => !current)}
                    // `touch-hit-44`: the icon is 20px, so on a coarse pointer the hit area is widened to the
                    // 44px floor without moving the byline (`relative` gives the expander its box).
                    className={cn(
                        "relative touch-hit-44 inline-flex size-5 shrink-0 items-center justify-center rounded-full align-middle text-muted-foreground transition-colors standard-hover focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50",
                        className,
                    )}
                >
                    <Icon aria-hidden={true} className="size-3.5" strokeWidth={2.25} />
                </button>
            </TooltipTrigger>
            <TooltipContent side="top" sideOffset={4} className="max-w-64">
                <span className="block font-medium">{name}</span>
                <span className="block opacity-80">{meaning}</span>
            </TooltipContent>
        </Tooltip>
    );
}

export default DistinctionBadge;
